import React from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ContactForm } from '../components/crm';
import { Contact, CreateContactDTO, UpdateContactDTO } from '../types/contact';
import { contactApi } from '../services/contactApi';
import { ContactListNavigationState } from '../types/contactNavigation';

const ContactFormPage: React.FC<{ mode: 'create' | 'edit' }> = ({ mode }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const [contact, setContact] = React.useState<Contact | null>(null);
  const [loading, setLoading] = React.useState(mode === 'edit');
  const contactListState = (location.state as { contactListState?: ContactListNavigationState } | null)?.contactListState ?? null;

  const navigateToContacts = React.useCallback(() => {
    if (contactListState) {
      navigate('/contacts', {
        state: { contactListState },
      });
      return;
    }

    navigate('/contacts');
  }, [contactListState, navigate]);

  React.useEffect(() => {
    const fetchContact = async () => {
      if (mode === 'edit' && id) {
        try {
          const data = await contactApi.get(id);
          setContact(data);
        } catch (error) {
          console.error('Error fetching contact:', error);
          navigateToContacts();
        } finally {
          setLoading(false);
        }
      }
    };

    fetchContact();
  }, [mode, id, navigateToContacts]);

  if (loading) {
    return <div className="loading">Loading contact...</div>;
  }

  const handleSubmit = async (data: CreateContactDTO | UpdateContactDTO) => {
    try {
      if (mode === 'edit' && contact) {
        await contactApi.update(contact.id, data as UpdateContactDTO);
      } else {
        await contactApi.create(data as CreateContactDTO);
      }
      navigateToContacts();
    } catch (error) {
      console.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} contact:`, error);
      throw error;
    }
  };

  return (
    <ContactForm
      contact={contact || undefined}
      onSubmit={handleSubmit}
      onCancel={navigateToContacts}
    />
  );
};

export default ContactFormPage;
